import React, { useState, useRef, useEffect, useCallback } from "react";
import { CameraIcon, FlipIcon } from "../icons/Index";

const WebcamModal = ({ docLabel, onCapture, onClose, onUploadClick }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("user"); // default front camera

  const startCamera = useCallback((mode) => {
    // Stop any existing stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
    setError(null);

    if (!navigator?.mediaDevices?.getUserMedia) {
      setError(true);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: mode, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            setReady(true);
          };
        }
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    startCamera("user");
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fix: compute next mode locally so we don't rely on stale state
  const flipCamera = () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);      // update state for next flip
    startCamera(next);    // start with the already-computed next value
  };

  const [capturedImage, setCapturedImage] = useState(null);

  const capture = () => {
    if (!videoRef.current || !ready) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setCapturedImage(dataUrl);
  };

  const retake = () => {
    setCapturedImage(null);
    startCamera(facing);
  };

  const confirmCapture = () => {
    onCapture(capturedImage);
  };

  const handleClose = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    onClose();
  };

  return (
    <div className="pd-webcam-overlay">
      <div className="pd-webcam-modal">

        {/* Header */}
        <div className="pd-webcam-header">
          <div className="pd-webcam-title-row">
            <CameraIcon size={15} color="white" />
            {docLabel ? `CAPTURE — ${docLabel}` : "TAKE PHOTO"}
          </div>
          <button type="button" className="pd-webcam-close" onClick={handleClose}>✕</button>
        </div>

        {/* Viewport */}
        <div className="pd-webcam-body">
          <div className="pd-webcam-viewport" style={{ height: "220px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            {capturedImage ? (
              <img src={capturedImage} alt="Captured preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "12px" }} />
            ) : error ? (
              <div className="pd-webcam-err">
                <div className="pd-webcam-err-icon">📷</div>
                <p className="pd-webcam-err-title">Camera Access Denied</p>
                <p className="pd-webcam-err-msg">
                  Please allow camera access in your browser settings and try again.
                </p>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="pd-webcam-video"
                />
                {ready && (
                  <>
                    <div className="pd-corner pd-corner--tl" />
                    <div className="pd-corner pd-corner--tr" />
                    <div className="pd-corner pd-corner--bl" />
                    <div className="pd-corner pd-corner--br" />
                    <button type="button" className="pd-webcam-flip-btn" onClick={flipCamera}
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        right: "10px",
                        borderRadius: "50%",
                        padding: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FlipIcon size={20} color="white" />
                    </button>
                  </>
                )}
                {ready && (
                  <>
                    <div className="pd-corner pd-corner--tl" />
                    <div className="pd-corner pd-corner--tr" />
                    <div className="pd-corner pd-corner--bl" />
                    <div className="pd-corner pd-corner--br" />
                  </>
                )}
              </>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="pd-webcam-foot">
          {capturedImage ? (
            <>
              <button
                type="button"
                className="pd-webcam-cancel"
                onClick={handleClose}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
              >
                Close
              </button>
              <button
                type="button"
                className="pd-webcam-cancel"
                onClick={retake}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
              >
                Retake
              </button>
              <button
                type="button"
                className="pd-webcam-capture pd-webcam-capture--on"
                onClick={confirmCapture}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", background: "#2a9d8f", border: "none" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Use Photo
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="pd-webcam-cancel"
                onClick={handleClose}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
              >
                Cancel
              </button>
              {onUploadClick && (
                <button
                  type="button"
                  className="pd-webcam-cancel"
                  onClick={onUploadClick}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Upload
                </button>
              )}
              <button
                type="button"
                onClick={capture}
                disabled={!ready || error}
                className={`pd-webcam-capture ${ready && !error ? "pd-webcam-capture--on" : "pd-webcam-capture--off"}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                  <circle cx="12" cy="13" r="4"></circle>
                </svg>
                Capture
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebcamModal;